<template>
  <div class="formulario-modelo">
    <LoadingOverlay :visible="loading" message="Cargando instrumentos..." />

    <div class="header-flex">
      <div>
        <h1>Subir evidencias</h1>
        <p v-if="nombreModelo" class="ctx-modelo">{{ nombreModelo }}</p>
      </div>
      <div class="botones">
        <button class="btn salir" @click="volver">Volver</button>
      </div>
    </div>

    <div class="divider"></div>

    <div v-if="!loading && error" class="error">{{ error }}</div>

    <div v-else-if="!loading">
      <!-- Filtro por tipo de información -->
      <div class="filtro-botones">
        <button
          v-for="t in opcionesFiltro"
          :key="t.label"
          :class="['btn-filtro', filtroTipoInfo === t.value ? 'activo' : '']"
          @click="filtroTipoInfo = t.value"
        >
          {{ t.label }}
        </button>
      </div>

      <p v-if="arbolFiltrado.length === 0" class="mensaje-vacio">
        No se encontraron instrumentos asociados a esta versión.
      </p>

      <div v-else class="aspectos-container">
        <div
          v-for="aspecto in arbolFiltrado"
          :key="aspecto.idComponente"
          class="aspecto-seccion"
        >
          <div class="aspecto-header">
            <span class="aspecto-ordinal">{{ formatOrdinal(aspecto.ordinal) }}</span>
            <span class="aspecto-nombre">{{ aspecto.nombre }}</span>
          </div>

          <div class="indicadores-lista">
            <div
              v-for="ind in aspecto.indicadores"
              :key="ind.idIndicador"
              class="indicador-bloque"
            >
              <div class="indicador-nombre-wrap">
                <span class="indicador-badge">Indicador</span>
                <span class="indicador-nombre">{{ ind.nombre }}</span>
              </div>

              <div
                v-for="(inst, idx) in ind.instrumentos"
                :key="idx"
                class="instrumento-card"
              >
                <div class="instrumento-info">
                  <span
                    v-if="inst.tipoLabel"
                    class="instrumento-tipo-badge"
                    :class="`tipo-${inst.tipoKey}`"
                  >
                    {{ inst.tipoLabel }}
                  </span>
                  <span class="instrumento-nombre">{{ inst.instrumentoNombre }}</span>
                </div>
                <button
                  class="btn guardar"
                  :disabled="!inst.idInstrumento || !inst.routeName"
                  @click="abrirFichaDirecta(inst, aspecto, ind)"
                >
                  {{ inst.tipoKey === 'opinion' ? 'Aplicar Instrumento' : 'Cargar ficha' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>

  <Teleport to="body">
    <div v-if="modalFicha" class="modal-ficha-overlay">
      <div class="modal-ficha-panel">
        <FichaDocumental
          v-if="modalFicha.tipo === 'documental'"
          :idInstrumento="modalFicha.idInstrumento"
          :nombreVersion="modalFicha.nombreVersion"
          :rutaComponente="modalFicha.rutaComponente"
          :nombreIndicador="modalFicha.nombreIndicador"
          @cerrar="modalFicha = null"
        />
        <FichaEstadistica
          v-if="modalFicha.tipo === 'estadistica'"
          :idInstrumento="modalFicha.idInstrumento"
          :nombreVersion="modalFicha.nombreVersion"
          :rutaComponente="modalFicha.rutaComponente"
          :nombreIndicador="modalFicha.nombreIndicador"
          @cerrar="modalFicha = null"
        />
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
/**
 * Carga de evidencias por instrumento.
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import FichaDocumental from '@/components/FichaDocumental.vue'
import FichaEstadistica from '@/components/FichaEstadistica.vue'

import { useGestionVersiones } from '@/store/GestionVersiones'
import { useGestionIndicadores } from '@/store/GestionIndicadores'
import { useGestionInstrumento } from '@/store/GestionInstrumentos'
import { useGestionIndicadorInstrumentos } from '@/store/GestionIndicadorInstrumentos'
import { useGestionTipoComponente } from '@/store/GestionTipoComponente'
import { useGestionIndicadorEvaluadores } from '@/store/GestionIndicadorEvaluadores'
import { useAutenticar } from '@/store/IngresoAutenticar'
import { useComponentes } from '@/store/GestionComponentes'

import type Instrumento from '@/interfaces/Instrumetos'
import type Indicador from '@/interfaces/Indicador'
import type Componente from '@/interfaces/Componente'

const route = useRoute()
const router = useRouter()

const idVersion = computed(() => Number(route.params.id_version ?? NaN))

const versionesStore = useGestionVersiones()
const indicadoresStore = useGestionIndicadores()
const instrumentosStore = useGestionInstrumento()
const indicadorInstrumentoStore = useGestionIndicadorInstrumentos()
const tipoComponenteStore = useGestionTipoComponente()
const evaluadoresStore = useGestionIndicadorEvaluadores()
const authStore = useAutenticar()
const componentesStore = useComponentes()

const loading = ref(false)
const error = ref<string | null>(null)
const nombreModelo = ref('')

interface RutaNodo { tipo: string; ordinal: string; nombre: string }

const modalFicha = ref<{
  tipo: 'documental' | 'estadistica'
  idInstrumento: number
  nombreVersion: string
  rutaComponente: RutaNodo[]
  nombreIndicador: string
} | null>(null)

const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

type TipoFiltroInfo = '' | 'documental' | 'cuantitativa' | 'opinion'

interface InstrumentoItem {
  idInstrumento: number | null
  // Un formulario de opinión es compartido por varios indicadores, así que el
  // instrumento por sí solo ya no identifica qué se va a aplicar.
  idIndicador: number | null
  instrumentoNombre: string
  tipoKey: Exclude<TipoFiltroInfo, ''> | null
  tipoLabel: string
  routeName: 'FichaDocumental' | 'FichaEstadistica' | 'FichaOpinion' | null
}

interface IndicadorItem {
  idIndicador: number
  nombre: string
  ordinal: number | null
  idTipoComponente: number
  instrumentos: InstrumentoItem[]
}

interface AspectoItem {
  idComponente: number
  nombre: string
  ordinal: string
  indicadores: IndicadorItem[]
}

const arbol = ref<AspectoItem[]>([])

const filtroTipoInfo = ref<TipoFiltroInfo>('')

const opcionesFiltro = computed(() => ([
  { label: 'Todos', value: '' as TipoFiltroInfo },
  { label: 'Documental', value: 'documental' as TipoFiltroInfo },
  { label: 'Cuantitativa', value: 'cuantitativa' as TipoFiltroInfo },
  { label: 'Opinión', value: 'opinion' as TipoFiltroInfo }
]))

const arbolFiltrado = computed<AspectoItem[]>(() => {
  if (!filtroTipoInfo.value) return arbol.value
  return arbol.value
    .map(aspecto => ({
      ...aspecto,
      indicadores: aspecto.indicadores
        .map(ind => ({
          ...ind,
          instrumentos: ind.instrumentos.filter(i => i.tipoKey === filtroTipoInfo.value)
        }))
        .filter(ind => ind.instrumentos.length > 0)
    }))
    .filter(aspecto => aspecto.indicadores.length > 0)
})

function tipoInfo(idTipo: number): { key: InstrumentoItem['tipoKey']; label: string; routeName: InstrumentoItem['routeName'] } {
  const tipo = tipoComponenteStore.tipos.find(t => t.id_tipo_componente === idTipo)
  const raw = String((tipo?.nombre ?? tipo?.tipo ?? '')).trim()
  const nombre = raw.toLowerCase()

  if (nombre.includes('documental')) {
    return { key: 'documental', label: raw || 'Documental', routeName: 'FichaDocumental' }
  }
  if (nombre.includes('opinión') || nombre.includes('opinion')) {
    return { key: 'opinion', label: raw || 'Opinión', routeName: 'FichaOpinion' }
  }
  if (nombre.includes('cuantit') || nombre.includes('estad')) {
    return { key: 'cuantitativa', label: raw || 'Cuantitativa', routeName: 'FichaEstadistica' }
  }
  return { key: null, label: raw || String(idTipo), routeName: null }
}

function abrirFichaDirecta(inst: InstrumentoItem, aspecto?: AspectoItem, ind?: IndicadorItem) {
  if (!inst.idInstrumento) {
    mostrarMensaje('Este indicador no tiene instrumento asignado.', 'error')
    return
  }
  if (!inst.routeName) {
    mostrarMensaje('No se pudo determinar el tipo de ficha para este indicador.', 'error')
    return
  }
  if (inst.tipoKey === 'documental' || inst.tipoKey === 'cuantitativa') {
    modalFicha.value = {
      tipo: inst.tipoKey === 'documental' ? 'documental' : 'estadistica',
      idInstrumento: inst.idInstrumento!,
      nombreVersion: nombreModelo.value,
      rutaComponente: aspecto ? buildRutaComponente(aspecto.idComponente) : [],
      nombreIndicador: ind?.nombre ?? ''
    }
    return
  }
  // Se propaga el indicador: el formulario es compartido, y sin esto la ficha
  // no sabe cuál de los indicadores vinculados se está aplicando.
  const rawEjecucion = Array.isArray(route.query.id_ejecucion) ? route.query.id_ejecucion[0] : route.query.id_ejecucion
  const idEjecucion = Number(rawEjecucion)
  router.push({
    name: inst.routeName,
    query: {
      id_instrumento: inst.idInstrumento,
      id_version: idVersion.value,
      ...(inst.idIndicador ? { id_indicador: inst.idIndicador } : {}),
      ...(Number.isFinite(idEjecucion) && idEjecucion > 0 ? { id_ejecucion: idEjecucion } : {})
    }
  })
}

function volver() {
  router.push({ name: 'ProcesoAutoevaluacionUsuario' })
}

function formatOrdinal(ordinal: string): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length <= 1 ? ordinal : parts.slice(1).join('.')
}

function buildRutaComponente(idComponente: number): RutaNodo[] {
  type NodoTemp = { idTipo: number | null | undefined; ordinal: string; nombre: string }
  const path: NodoTemp[] = []
  function buscar(nodos: Componente[], target: number, padres: NodoTemp[]): boolean {
    for (const n of nodos) {
      const nodoTemp: NodoTemp = { idTipo: n.id_tipo_componente, ordinal: formatOrdinal(n.ordinal ?? ''), nombre: n.nombre }
      if (n.id_componente === target) {
        padres.forEach(p => path.push(p))
        path.push(nodoTemp)
        return true
      }
      if (n.children?.length && buscar(n.children, target, [...padres, nodoTemp])) return true
    }
    return false
  }
  const raiz = componentesStore.componentes[0]
  if (raiz?.children) buscar(raiz.children, idComponente, [])
  return path.map(p => ({
    tipo: tipoComponenteStore.tipos.find(t => t.id_tipo_componente === p.idTipo)?.nombre ?? '',
    ordinal: p.ordinal,
    nombre: p.nombre
  }))
}

function flattenComponentes(lista: Componente[], mapa: Map<number, { nombre: string; ordinal: string }>) {
  for (const c of lista) {
    if (c.id_componente) {
      mapa.set(c.id_componente, { nombre: c.nombre, ordinal: c.ordinal ?? '' })
    }
    if (c.children?.length) {
      flattenComponentes(c.children, mapa)
    }
  }
}

async function cargarInstrumentosDeVersion() {
  if (!Number.isFinite(idVersion.value) || idVersion.value <= 0) {
    error.value = 'ID de versión inválido.'
    return
  }

  loading.value = true
  error.value = null

  try {
    arbol.value = []

    const idUsuario = authStore.usuarioActual?.id

    // Todas las cargas independientes en paralelo: solo la cadena
    // cargarRaizPorModelo → cargarArbolDesdeRaiz necesita esperar a version.
    const [, version] = await Promise.all([
      tipoComponenteStore.getTipos(),
      versionesStore.getVersionById(idVersion.value),
      indicadoresStore.cargarTodos(),
      instrumentosStore.cargarTodos(),
      indicadorInstrumentoStore.getRelaciones(),
      idUsuario
        ? evaluadoresStore.cargar({ id_version: idVersion.value, id_usuario: idUsuario })
        : Promise.resolve()
    ])
    nombreModelo.value = version?.nombre ?? ''

    // Árbol de componentes: secuencial porque cada paso depende del anterior
    const idModelo = (version as { id_modelo?: number } | null)?.id_modelo ?? null
    if (idModelo) {
      const raiz = await componentesStore.cargarRaizPorModelo(idModelo)
      if (raiz?.id_componente) {
        await componentesStore.cargarArbolDesdeRaiz(raiz.id_componente)
      }
    }

    const componenteMap = new Map<number, { nombre: string; ordinal: string }>()
    flattenComponentes(componentesStore.componentes, componenteMap)

    // Filtrar indicadores de la versión según permisos
    let indicadoresVersion: Indicador[]

    const idsEvidencia = new Set(
      evaluadoresStore.asignaciones
        .filter(a => (a.tarea === 'evidencias' || a.tarea === 'instrumentos') && a.id_indicador != null)
        .map(a => a.id_indicador as number)
    )

    if (idsEvidencia.size > 0) {
      indicadoresVersion = indicadoresStore.indicadores.filter(
        i => i.id_version === idVersion.value && idsEvidencia.has(i.id_indicador)
      )
    } else if (authStore.esAdmin) {
      indicadoresVersion = indicadoresStore.indicadores.filter(i => i.id_version === idVersion.value)
    } else {
      const idsAsignados = new Set(
        evaluadoresStore.asignaciones
          .map(a => a.id_indicador)
          .filter((id): id is number => id != null)
      )
      if (idsAsignados.size === 0) { arbol.value = []; return }
      indicadoresVersion = indicadoresStore.indicadores.filter(
        i => i.id_version === idVersion.value && idsAsignados.has(i.id_indicador)
      )
    }

    const idsIndicadoresVersion = new Set(indicadoresVersion.map(i => i.id_indicador))
    const instrumentosPorIndicador = new Map<number, number[]>()
    for (const rel of indicadorInstrumentoStore.relaciones) {
      if (!idsIndicadoresVersion.has(rel.id_indicador)) continue
      const lista = instrumentosPorIndicador.get(rel.id_indicador) ?? []
      if (!lista.includes(rel.id_instrumento)) lista.push(rel.id_instrumento)
      instrumentosPorIndicador.set(rel.id_indicador, lista)
    }

    // Agrupar indicadores por componente
    const porComponente = new Map<number, IndicadorItem[]>()
    for (const ind of indicadoresVersion) {
      const idsInst = instrumentosPorIndicador.get(ind.id_indicador) ?? []
      const info = tipoInfo(ind.id_tipo_componente)

      const instrumentos: InstrumentoItem[] = idsInst.length === 0
        ? [{
            idInstrumento: null,
            idIndicador: ind.id_indicador,
            instrumentoNombre: 'Sin instrumento asignado',
            tipoKey: info.key,
            tipoLabel: info.label,
            routeName: info.routeName
          }]
        : idsInst.map(idInst => {
            const inst = instrumentosStore.instrumentos.find(i => i.id_instrumento === idInst) as Instrumento | undefined
            return {
              idInstrumento: idInst,
              idIndicador: ind.id_indicador,
              // Se prefiere la descripción: ahí va el nombre del formulario
              // ("Encuesta estudiantes"). `herramienta` es solo el tipo
              // ("encuesta") y no distingue un formulario de otro.
              instrumentoNombre: (inst as any)?.descripcion || inst?.herramienta || String(idInst),
              tipoKey: info.key,
              tipoLabel: info.label,
              routeName: info.routeName
            }
          })

      const idComp = ind.id_componente
      if (!porComponente.has(idComp)) porComponente.set(idComp, [])
      porComponente.get(idComp)!.push({
        idIndicador: ind.id_indicador,
        nombre: ind.nombre ?? ind.enunciado,
        ordinal: ind.ordinal ?? null,
        idTipoComponente: ind.id_tipo_componente,
        instrumentos
      })
    }

    // Ordenar indicadores dentro de cada componente por su ordinal
    for (const inds of porComponente.values()) {
      inds.sort((a, b) => (a.ordinal ?? 9999) - (b.ordinal ?? 9999))
    }

    // Construir y ordenar aspectos por ordinal numérico
    const nuevaArbol: AspectoItem[] = []
    for (const [idComp, inds] of porComponente) {
      const compInfo = componenteMap.get(idComp)
      nuevaArbol.push({
        idComponente: idComp,
        nombre: compInfo?.nombre ?? `Componente ${idComp}`,
        ordinal: compInfo?.ordinal ?? '',
        indicadores: inds
      })
    }

    nuevaArbol.sort((a, b) =>
      (a.ordinal).localeCompare(b.ordinal, undefined, { numeric: true })
    )

    arbol.value = nuevaArbol
  } catch (e: any) {
    error.value = e?.message || 'Error al cargar instrumentos.'
  } finally {
    loading.value = false
  }
}

onMounted(cargarInstrumentosDeVersion)
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
  max-width: 1400px;
  min-width: 500px;
}

.ctx-modelo {
  display: inline-block;
  margin: 6px 0 0;
  padding: 3px 10px;
  background: #e8f0f8;
  border-left: 3px solid #0e69a0;
  border-radius: 3px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #111;
}

.header-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.subtitulo {
  margin: 0.25rem 0 0;
  font-size: 1rem;
  font-weight: normal;
  color: #666;
}

.divider {
  width: 100%;
  height: 1px;
  background-color: #e0e0e0;
  margin: 1rem 0;
}

.error {
  padding: 1rem;
  color: #b00020;
}

/* Filtros */
.filtro-botones {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.btn-filtro {
  padding: 0.45rem 1.1rem;
  border-radius: 20px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.btn-filtro:hover {
  background-color: #eea222;
  color: white;
}

.btn-filtro.activo {
  background-color: #0e69a0;
  color: white;
  border-color: #0e69a0;
}

/* Árbol jerárquico */
.aspectos-container {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.aspecto-seccion {
  border: 1px solid #d8e3ea;
  border-radius: 8px;
  overflow: hidden;
}

.aspecto-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: #0e69a0;
  color: white;
  padding: 0.7rem 1.25rem;
}

.aspecto-ordinal {
  font-size: 1rem;
  font-weight: 800;
  opacity: 0.8;
  min-width: 2rem;
  flex-shrink: 0;
}

.aspecto-nombre {
  font-size: 0.95rem;
  font-weight: 700;
  flex: 1;
  overflow-wrap: break-word;
  min-width: 0;
}

/* Indicadores */
.indicadores-lista {
  display: flex;
  flex-direction: column;
}

.indicador-bloque {
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid #eef0f2;
  background-color: white;
}

.indicador-bloque:last-child {
  border-bottom: none;
}

.indicador-nombre-wrap {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  margin-bottom: 0.6rem;
}

.indicador-badge {
  font-size: 0.68rem;
  font-weight: 700;
  background: #e8f1f8;
  color: #0e69a0;
  padding: 0.12rem 0.45rem;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  flex-shrink: 0;
}

.indicador-nombre {
  font-size: 0.93rem;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.4;
}

/* Instrumentos */
.instrumento-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #f5f8fb;
  border: 1px solid #dce6ee;
  border-radius: 6px;
  padding: 0.55rem 0.9rem;
  margin-top: 0.45rem;
}

.instrumento-info {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex: 1;
  min-width: 0;
}

.instrumento-tipo-badge {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.12rem 0.45rem;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  flex-shrink: 0;
}

.tipo-documental  { background: #e8f5e9; color: #2e7d32; }
.tipo-cuantitativa { background: #e3f2fd; color: #1565c0; }
.tipo-opinion     { background: #fff3e0; color: #e65100; }

.instrumento-nombre {
  font-size: 0.88rem;
  color: #444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Botones */
.btn {
  padding: 0.45rem 0.85rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  transition: all var(--transition-fast);
  white-space: nowrap;
  flex-shrink: 0;
}

.btn.guardar {
  background-color: #eea222;
  color: black;
}

.btn.guardar:hover:not(:disabled) {
  background-color: #0e69a0;
  color: white;
}

.btn.guardar:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.btn.salir {
  background-color: white;
  border: 1px solid #ccc;
}

.btn.salir:hover {
  background-color: rgb(212, 26, 26);
  color: white;
}

.mensaje-vacio {
  margin: 0;
  padding: 0.5rem 0;
  color: #555;
}

/* Modal ficha */
.modal-ficha-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 2rem 1rem;
  box-sizing: border-box;
}

.modal-ficha-panel {
  width: 100%;
  max-width: 960px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}
</style>
